import './styles/graphic.scss';

import rawData from './data/data.json';

// the sheet names a photo by filename, so every file in the folder has to be
// bundled up front rather than imported one by one
const IMAGES = require.context('./images', false, /\.(png|jpe?g|gif|webp|svg)$/);

function imageUrl(file) {
  const name = String(file || '').trim();
  if (!name) return null;
  try {
    const asset = IMAGES(`./${name}`);
    return asset && asset.default ? asset.default : asset;
  } catch (err) {
    // a filename in the sheet that nobody has dropped in src/images yet
    console.warn(`kushner-timeline: no image named "${name}" in src/images`);
    return null;
  }
}

// Photos arrive after the copy has been measured, and the desktop layout is
// built on those measurements, so anything that depends on height has to be
// worked out again once they land.
function whenImagesLoad(root, run) {
  const pending = root.selectAll('img').nodes().filter(img => !img.complete);
  if (!pending.length) return;

  let left = pending.length;
  const settle = () => {
    left -= 1;
    if (left === 0) run();
  };
  pending.forEach((img) => {
    img.addEventListener('load', settle, { once: true });
    img.addEventListener('error', settle, { once: true });
  });
}

// hides quotes on mobile, where a 22-date, two-lane timeline runs long;
// flip to true to restore them at that width
const SHOW_QUOTES_MOBILE = true;

// the timeline loads truncated, behind a "show full timeline" button; flip to
// false to open it in full while working on the entries and pictures
const START_COLLAPSED = true;

// Desktop names the lanes once, in the column titles above the timeline.
// Mobile has no such header, so the first entry in each lane carries the full
// title and every one after it takes the short form.
const LANE_TITLE = {
  role: 'Kushner’s government role',
  biz: 'Kushner’s business activities',
};

const LANE_TITLE_SHORT = {
  role: 'Government role',
  biz: 'Business activities',
};

const MONTH_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// AP abbreviates all but the short months
const MONTH_LABEL = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

const DAY_MS = 86400000;

// war bands lead their date, since they push both columns down past them
const LANE_ORDER = { war: 0, role: 1, biz: 2 };

// how many vertical pixels one day of real elapsed time is worth; the
// desktop timeline is positioned on this scale rather than by row order.
// Set high enough that a typical entry fits inside the gap its own date
// opens up, so most entries land on their true position untouched.
const PX_PER_DAY = 1;

// breathing room kept below an entry before the next one in the same lane
const ENTRY_GAP = 22;

// how far the next date has to sit below the one before it when the two are
// in opposite columns — enough that the step down reads as "and then this"
const DATE_STEP = 46;

// A cell the editor formatted as a date, rather than text, comes through as a
// spreadsheet serial — days counted from Dec. 30, 1899. Left alone it prints
// as a bare number and falls off the scale, taking its row off desktop
// altogether, so it's written back out in the style the other cells use.
function normalizeDate(value) {
  const clean = String(value == null ? '' : value).trim();
  if (!/^\d{4,6}$/.test(clean)) return clean;

  const utc = new Date(Date.UTC(1899, 11, 30) + Number(clean) * DAY_MS);
  return `${MONTH_LABEL[utc.getUTCMonth()]} ${utc.getUTCDate()}, ${utc.getUTCFullYear()}`;
}

// editors write dates as free text ("Jan. 2025", "Sept. 16, 2025",
// "Oct. 9–10, 2025"), so parse to a real Date to place them on the scale.
// A day range takes its first day; a month with no day sits mid-month.
function parseDate(str) {
  // a date cell that fails to parse drops its row off the scale entirely, so
  // trailing whitespace from the sheet can't be allowed to reach the patterns
  const clean = String(str == null ? '' : str).trim();

  const withDay = clean.match(/^([A-Za-z]+)\.?\s+(\d{1,2})(?:\s*[–-]\s*\d{1,2})?,\s*(\d{4})$/);
  if (withDay) {
    const month = MONTH_INDEX[withDay[1].slice(0, 3).toLowerCase()];
    if (month !== undefined) return new Date(Number(withDay[3]), month, Number(withDay[2]));
  }

  const monthOnly = clean.match(/^([A-Za-z]+)\.?\s+(\d{4})$/);
  if (monthOnly) {
    const month = MONTH_INDEX[monthOnly[1].slice(0, 3).toLowerCase()];
    if (month !== undefined) return new Date(Number(monthOnly[2]), month, 15);
  }


  return null;
}

// the scale starts at the first of the earliest entry's month, so the
// first month label lands at y=0 rather than above the top of the track
function scaleOrigin(minTime) {
  const origin = new Date(minTime);
  origin.setDate(1);
  origin.setHours(0, 0, 0, 0);
  return origin.getTime();
}

function timeToY(time, originTime) {
  return ((time - originTime) / DAY_MS) * PX_PER_DAY;
}

// Everything sharing a date sits at the same height, and every entry starts
// from the y its own date maps to, so the spine keeps a roughly even pace.
// The columns are packed separately, though: a long entry crowds the rest of
// its own column down but leaves the other side alone, which is what keeps a
// half-empty column from opening a hole beside a full one.
//
// Packing them separately would let the fuller column fall behind the emptier
// one, until a later date on the left printed level with an earlier one on the
// right. So each date also has to sit a clear step below the date before it,
// whichever column either of them is in: the page always reads in order,
// top to bottom, across both columns.
//
// Each date wants the y its own position on the scale maps to; if the copy
// above it is too tall to fit in that gap, it slides down just enough to
// clear, and the next date is free to snap back to its true position.
//
// What lands on the scale is the entry's dot, which sits on the headline
// rather than on the date line above it, so each entry is offset upwards
// by however far its own dot sits below its top edge.
function layoutEntries(rows, originTime, metricsOf) {
  const byTime = [];
  rows.forEach((row) => {
    const group = byTime.find(g => g.time === row.time);
    if (group) group.rows.push(row);
    else byTime.push({ time: row.time, rows: [row] });
  });

  byTime.sort((a, b) => a.time - b.time);

  const placed = new Map();
  // each column keeps its own cursor, so a tall entry only pushes down the
  // column it's in; a war band spans both and so resets both
  const freeY = { role: 0, biz: 0 };
  // the line the previous date landed on, in either column
  let lastAnchor = -Infinity;

  byTime.forEach(({ time, rows: sameDate }) => {
    const idealY = timeToY(time, originTime);

    // war bands are full width, so they stack above whatever shares their
    // date rather than sitting alongside it
    const wars = sameDate.filter(r => r.lane === 'war');
    const sides = sameDate.filter(r => r.lane !== 'war');

    if (wars.length) {
      let cursor = Math.max(idealY, freeY.role, freeY.biz, lastAnchor + DATE_STEP);
      wars.forEach((row) => {
        placed.set(row, cursor);
        cursor += metricsOf(row).height + ENTRY_GAP;
      });
      freeY.role = cursor;
      freeY.biz = cursor;
      lastAnchor = cursor;
    }

    if (!sides.length) return;

    // the dot has to clear the copy above it, so the whole group drops by
    // the largest dot offset among the entries sharing this date, plus room
    // for any photo standing above the copy
    const floor = Math.max(...sides.map(r => freeY[r.lane]));
    const maxOffset = Math.max(...sides.map(r => metricsOf(r).anchorOffset + metricsOf(r).rise));
    const anchorY = Math.max(idealY, floor + maxOffset, lastAnchor + DATE_STEP);
    lastAnchor = anchorY;

    sides.forEach((row) => {
      const { height, anchorOffset, overhang } = metricsOf(row);
      const y = anchorY - anchorOffset;
      placed.set(row, y);
      // a photo hangs out of the flow, so it can't push the far column around,
      // but the rest of its own column still has to clear it
      freeY[row.lane] = y + height + overhang + ENTRY_GAP;
    });
  });

  return rows.map(row => ({ ...row, y: placed.get(row) }));
}

// rows sharing a date collapse into one date-group (mobile only): war rows
// render as a full-width band, then role/biz stack beneath it.
//
// Desktop reads the dates themselves to place entries on its time scale, so
// mobile has to sort as well, or a row typed into the sheet out of sequence
// runs in sequence on one breakpoint and out of it on the other. A row whose
// date won't parse keeps company with the last dated row above it rather than
// being flung to one end.
function groupByDate(rows) {
  let lastTime = -Infinity;
  const ordered = rows
    .map((row, index) => {
      const parsed = parseDate(row.date);
      if (parsed) lastTime = parsed.getTime();
      return { row, index, time: lastTime };
    })
    .sort((a, b) => a.time - b.time
      || LANE_ORDER[a.row.lane] - LANE_ORDER[b.row.lane]
      || a.index - b.index)
    .map(item => item.row);

  const groups = [];
  let current = null;

  ordered.forEach((row) => {
    if (!current || current.date !== row.date) {
      current = {
        date: row.date, dateUrl: row.url, war: [], role: null, biz: null,
      };
      groups.push(current);
    }
    if (row.lane === 'war') {
      current.war.push(row);
    } else {
      current[row.lane] = row;
    }
  });

  return groups;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// editors can wrap a phrase in a sheet cell as "{like this}" and put a URL
// in the row's `url` column to turn just that phrase into a link; text is
// escaped first, so braces are the only markup this recognizes. If the row
// has no url, the braces are stripped but the phrase still prints plainly,
// rather than leaking literal "{...}" onto the page.
function linkify(text, url) {
  const escaped = escapeHtml(text);
  if (!url) return escaped.replace(/\{([^}]+)\}/g, '$1');
  return escaped.replace(
    /\{([^}]+)\}/g,
    (match, label) => `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
}

// summaries are written as sentences and should close like one, but the sheet
// is edited in a hurry and cells often come back without the period. A phrase
// that ends on a quotation mark takes the period inside it, per AP.
function withStop(text) {
  const str = String(text == null ? '' : text).trim();
  if (!str || /[.!?:;]$/.test(str)) return str;

  const quoted = str.match(/^([\s\S]*[^\s])\s*([”"])$/);
  if (quoted) return /[.!?]$/.test(quoted[1]) ? str : `${quoted[1]}.${quoted[2]}`;

  return `${str}.`;
}

// desktop labels each entry with its own specific date; mobile labels it
// with the lane's full name (isCategoryTitle), since the date already sits
// above the group there.
//
// The dot rides the headline on desktop, so it lands on the timeline at
// the headline rather than a line above it; on mobile it stays inline in
// the lane tag, where the rail runs through it.
// both spellings of each direction, since the two position columns invite
// "above/below" in one and "top/bottom" in the other
const ABOVE_WORDS = ['above', 'top'];
const CAPTION_SIDES = ['top', 'left', 'right'];

const sheetWord = value => String(value == null ? '' : value).trim().toLowerCase();

// The sheet's image-position column says whether the photo leads the entry or
// follows it; anything else, blank included, reads as below. caption-position
// moves the caption off its default spot under the picture, and img-max-width
// caps a photo that shouldn't run the full column. No lazy loading: the
// desktop layout measures these to place entries on the time scale, and a
// photo that arrives once the reader scrolls to it would land on top of its
// neighbours.
function appendFigure(cell, entry, isMobile) {
  const src = imageUrl(entry.image);
  if (!src) return;

  // A mobile override wins at that width when it's filled in; a blank one
  // leaves the entry on its desktop setting. The sheet spells them both ways —
  // mobile-image-position but img-max-width-mobile — so either is honored.
  const override = (key) => {
    if (!isMobile) return '';
    const prefixed = entry[`mobile-${key}`];
    return prefixed == null || prefixed === '' ? entry[`${key}-mobile`] : prefixed;
  };

  const position = key => sheetWord(override(key)) || sheetWord(entry[key]);

  const above = ABOVE_WORDS.indexOf(position('image-position')) > -1;
  const rawSide = position('caption-position');
  const side = CAPTION_SIDES.indexOf(rawSide) > -1 ? rawSide : null;
  const maxWidth = parseFloat(override('img-max-width')) || parseFloat(entry['img-max-width']);
  const beside = side === 'left' || side === 'right';

  // a class off the filename, so a single picture can take a treatment that
  // isn't worth a column of its own — see the report screenshot in graphic.scss
  const slug = sheetWord(entry.image).replace(/\.[^.]+$/, '').replace(/[^a-z0-9]+/g, '-');

  const figure = above ? cell.insert('figure', ':first-child') : cell.append('figure');
  figure.attr('class', `entry-figure img-${slug}${above ? ' is-above' : ''}${side ? ` cap-${side}` : ''}`);

  // the caption carries the description, so alt text would only repeat it
  const img = figure.append('img').attr('src', src).attr('alt', '');

  // On desktop a caption stacked under a narrowed photo is capped with it, so
  // the two read as one object. On mobile there's only one column to play
  // with, so the cap stays on the picture and the caption runs the full width.
  // A caption set beside the photo always takes the width the photo gives up.
  if (maxWidth) (beside || isMobile ? img : figure).style('max-width', `${maxWidth}px`);

  if (entry.caption) {
    figure.append('figcaption')
      .attr('class', 'entry-caption')
      .html(linkify(entry.caption, entry.url));
  }
}

function appendEntry(cell, entry, laneClass, tagText, isCategoryTitle, isMobile) {
  const tagClass = `entry-tag ${laneClass}${isCategoryTitle ? ' category-title' : ''}`;
  const tag = cell.append('div').attr('class', tagClass);
  if (isCategoryTitle) {
    tag.append('span').attr('class', 'entry-dot').attr('aria-hidden', 'true');
  }
  tag.append('span').html(linkify(tagText, entry.url));

  const hed = cell.append('div').attr('class', 'entry-hed');
  if (!isCategoryTitle) {
    hed.append('span').attr('class', 'entry-dot').attr('aria-hidden', 'true');
  }
  hed.append('span').html(linkify(entry.hed, entry.url));

  if (entry.sum) {
    cell.append('div').attr('class', 'entry-sum').html(linkify(withStop(entry.sum), entry.url));
  }

  if (entry.quote) {
    cell.append('div').attr('class', 'entry-quote').html(linkify(entry.quote, entry.url));
    // A quote usually carries an attribution line, but not always; an empty
    // source column should leave no gap under the quote
    if (String(entry.source || '').trim()) {
      cell.append('div').attr('class', 'entry-source').html(linkify(entry.source, entry.url));
    }
  }

  if (entry.image) appendFigure(cell, entry, isMobile);
}

function appendWarBand(parent, warRows, dateHtml) {
  const band = parent.append('div').attr('class', 'war-band');
  if (dateHtml) {
    band.append('div').attr('class', 'war-date').html(dateHtml);
  }
  warRows.forEach((entry) => {
    const item = band.append('div').attr('class', 'war-item');
    item.append('span').attr('class', 'war-hed').html(linkify(entry.hed, entry.url));
    if (entry.sum) {
      item.append('div').attr('class', 'war-sum').html(linkify(withStop(entry.sum), entry.url));
    }
  });
}

// a time-scaled timeline: the spine is a real vertical time axis, labelled
// by month down its left, with role entries branching left and business
// entries branching right at the height their own date maps to
function drawDesktop(wrap, rows) {
  const header = wrap.append('div').attr('class', 'lane-header');
  header.append('div').attr('class', 'lane-label role').text(LANE_TITLE.role);
  header.append('div').attr('class', 'spine-spacer');
  header.append('div').attr('class', 'lane-label biz').text(LANE_TITLE.biz);

  const dated = rows
    .map((row) => {
      const parsed = parseDate(row.date);
      return { ...row, time: parsed ? parsed.getTime() : null };
    })
    .filter(row => row.time !== null)
    .sort((a, b) => a.time - b.time || LANE_ORDER[a.lane] - LANE_ORDER[b.lane]);

  const originTime = scaleOrigin(dated[0].time);

  const track = wrap.append('div').attr('class', 'tl-track');
  track.append('div').attr('class', 'tl-line').attr('aria-hidden', 'true');

  // first pass: drop every entry at the y its own date maps to, so the
  // copy lays out at its real column width and can be measured
  const nodes = new Map();

  dated.forEach((entry) => {
    const isWar = entry.lane === 'war';
    const node = track.append('div')
      .attr('class', isWar ? 'tl-entry war-entry' : `tl-entry lane-cell ${entry.lane}-cell`)
      .style('top', `${timeToY(entry.time, originTime)}px`);

    if (isWar) {
      appendWarBand(node, [entry], linkify(entry.date, entry.url));
    } else {
      appendEntry(node, entry, entry.lane, entry.date);
    }

    nodes.set(entry, node);
  });

  // second pass: measure what the copy actually came out to — both its
  // height and how far its dot sits below its top edge — then lay it out
  // again against those numbers so nothing collides
  const placeEntries = () => {
    const metrics = new Map();
    dated.forEach((entry) => {
      const box = nodes.get(entry).node().getBoundingClientRect();
      const dot = nodes.get(entry).select('.entry-dot').node();
      const dotBox = dot ? dot.getBoundingClientRect() : null;
      // a photo hangs out of the flow (see graphic.scss) and so counts for
      // nothing here, but the track still has to end below one that reaches
      // past the last entry
      const figure = nodes.get(entry).select('.entry-figure').node();
      const figBox = figure ? figure.getBoundingClientRect() : null;
      metrics.set(entry, {
        height: box.height,
        anchorOffset: dotBox ? (dotBox.top + dotBox.bottom) / 2 - box.top : 0,
        overhang: figBox ? Math.max(0, figBox.bottom - box.bottom) : 0,
        rise: figBox ? Math.max(0, box.top - figBox.top) : 0,
      });
    });

    const metricsOf = row => metrics.get(row)
      || {
        height: 0, anchorOffset: 0, overhang: 0, rise: 0,
      };
    let trackHeight = 0;

    layoutEntries(dated, originTime, metricsOf).forEach((row, i) => {
      const entry = dated[i];
      nodes.get(entry).style('top', `${row.y}px`);
      const m = metricsOf(entry);
      trackHeight = Math.max(trackHeight, row.y + m.height + m.overhang);
    });

    track.style('height', `${trackHeight}px`);
  };

  placeEntries();
  // a photo that finishes loading after that pass makes its entry taller than
  // the number the layout was built on, so the whole track is worked out again
  whenImagesLoad(track, placeEntries);
}

// a single rail runs down the left; each entry's dot sits on it, with the
// date and content stacked to the right
function drawMobile(wrap, groups) {
  // the long title introduces each lane, then gives way to the short one
  const introduced = {};
  const laneTitle = (lane) => {
    const title = introduced[lane] ? LANE_TITLE_SHORT[lane] : LANE_TITLE[lane];
    introduced[lane] = true;
    return title;
  };

  groups.forEach((group, i) => {
    // the fade overlay is appended after the rows, so :last-child never lands
    // on the closing row — it gets marked here instead
    const last = i === groups.length - 1 ? ' is-last' : '';
    const rowClass = `timeline-row is-mobile${group.war.length ? ' has-war' : ''}${last}`;
    const row = wrap.append('div').attr('class', rowClass);
    row.append('div').attr('class', 'tl-line-mobile').attr('aria-hidden', 'true');

    // a war row's date sits inside the band's fill, so the two are wrapped
    // together in one block that carries a single unbroken background across
    // both. The block spans the full row, rail included.
    const dateParent = group.war.length
      ? row.append('div').attr('class', 'war-block')
      : row;
    dateParent.append('div').attr('class', 'row-date').html(linkify(group.date, group.dateUrl));
    if (group.war.length) {
      appendWarBand(dateParent, group.war);
    }

    if (group.role || group.biz) {
      const content = row.append('div').attr('class', 'row-content');
      if (group.role) {
        appendEntry(content.append('div').attr('class', 'lane-block'), group.role, 'role', laneTitle('role'), true, true);
      }
      if (group.biz) {
        appendEntry(content.append('div').attr('class', 'lane-block'), group.biz, 'biz', laneTitle('biz'), true, true);
      }
    }
  });
}

// The timeline opens truncated: a first stretch under a fade, with the rest a
// button away. Heights are the visible window, chosen to cut mid-entry so the
// fade reads as "there's more" rather than as the end of the graphic.
const COLLAPSED_HEIGHT = { mobile: 540, desktop: 620 };
// below this much overflow the button costs the reader more than it saves
const WORTH_HIDING = 140;
const WRAP_ID = 'kushner-timeline';
const BODY_ID = 'kushner-timeline-body';

function addExpander(wrap, body, isMobile) {
  const collapsed = isMobile ? COLLAPSED_HEIGHT.mobile : COLLAPSED_HEIGHT.desktop;
  if (body.node().getBoundingClientRect().height < collapsed + WORTH_HIDING) return;

  body.append('div').attr('class', 'tl-fade').attr('aria-hidden', 'true');

  const button = wrap.append('button')
    .attr('class', 'tl-more')
    .attr('type', 'button')
    .attr('aria-controls', BODY_ID);

  const apply = (open) => {
    wrap.classed('is-collapsed', !open);
    body.style('max-height', open ? null : `${collapsed}px`);
    button.attr('aria-expanded', open ? 'true' : 'false')
      .text(open ? 'Show less' : 'Show full timeline');
    // the iframe is sized to its content, so the host page has to be told
    if (window.pymChild) window.pymChild.sendHeight();
  };

  apply(!START_COLLAPSED);

  button.on('click', () => {
    const opening = wrap.classed('is-collapsed');
    apply(opening);
    // collapsing from the foot of a long timeline would otherwise leave the
    // reader parked below the whole graphic
    if (!opening && window.pymChild && window.pymChild.scrollParentToChildEl) {
      window.pymChild.scrollParentToChildEl(WRAP_ID);
    }
  });
}

export default function (props, container, isMobile) {
  let data = rawData.map(row => ({ ...row, date: normalizeDate(row.date) }));
  if (isMobile && !SHOW_QUOTES_MOBILE) {
    data = data.map(row => ({ ...row, quote: '', source: '' }));
  }

  const wrap = container.append('div')
    .attr('class', `kushner-timeline${isMobile ? ' is-mobile' : ''}`)
    .attr('id', WRAP_ID);
  const body = wrap.append('div').attr('class', 'tl-body').attr('id', BODY_ID);

  if (isMobile) {
    drawMobile(body, groupByDate(data));
  } else {
    drawDesktop(body, data);
  }

  addExpander(wrap, body, isMobile);

  // the host iframe was sized against copy alone, before the photos landed
  whenImagesLoad(body, () => {
    if (window.pymChild) window.pymChild.sendHeight();
  });
}
